import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../utils/formatters';
import axios from 'axios';
import { FiX, FiPlay, FiDownload, FiUpload, FiInfo, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import './OracleUtilModal.css';

// Oracle 네이티브 유틸리티(exp/imp/expdp/impdp) 실행 화면.
//
// 이 화면에서 가장 중요한 것은 **덤프 파일이 어디에 생기는지** 를 오해하지 않게 하는 것이다.
//   exp/imp    → LiveSQL 서버 로컬. 브라우저로 받고 올릴 수 있다.
//   expdp/impdp → DB 서버의 DIRECTORY. LiveSQL 이 가져올 수 없다.
// 탭을 옮길 때마다 그 사실을 배너로 계속 보여준다.

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const API_BASE = `${API_ROOT}/sql-developer`;
const UTIL_API = `${API_BASE}/oracle-util`;

const getKinds = (t) => [
    { key: 'exp', label: t('dataTransfer:ora.exp'), local: true },
    { key: 'imp', label: t('dataTransfer:ora.imp'), local: true, write: true },
    { key: 'expdp', label: t('dataTransfer:ora.expdp'), local: false },
    { key: 'impdp', label: t('dataTransfer:ora.impdp'), local: false, write: true },
];

const OracleUtilModal = ({ isOpen, onClose, connectionId, connectionName }) => {
    const { t } = useTranslation();
    const [kind, setKind] = useState('exp');
    const [status, setStatus] = useState(null);
    const [directories, setDirectories] = useState({ directories: [], hint: null });
    const [jobs, setJobs] = useState([]);
    const [activeJob, setActiveJob] = useState(null);   // { job, log }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // 공통 옵션
    const [mode, setMode] = useState('tables');
    const [names, setNames] = useState('');            // 테이블/스키마 목록 (쉼표)
    const [directory, setDirectory] = useState('');
    const [dumpFile, setDumpFile] = useState('');
    const [uploaded, setUploaded] = useState(null);    // imp 용 { serverPath, name, size }
    const [fromUser, setFromUser] = useState('');
    const [toUser, setToUser] = useState('');
    const [tableExistsAction, setTableExistsAction] = useState('SKIP');

    const meta = getKinds(t).find(k => k.key === kind);
    const isWrite = !!meta?.write;
    const isDataPump = !meta?.local;

    const loadStatus = useCallback(async () => {
        try { setStatus((await axios.get(`${UTIL_API}/status`)).data); }
        catch (err) { setError(err.response?.data?.error || err.message); }
    }, []);

    const loadDirectories = useCallback(async () => {
        if (!connectionId) return;
        try { setDirectories((await axios.get(`${UTIL_API}/directories`, { params: { connectionId } })).data); }
        catch (err) { setDirectories({ directories: [], hint: err.response?.data?.error || err.message }); }
    }, [connectionId]);

    const loadJobs = useCallback(async () => {
        try { setJobs((await axios.get(`${UTIL_API}/jobs`)).data.jobs || []); }
        catch (_) { /* 목록 실패는 조용히 — 실행에는 영향 없음 */ }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        loadStatus(); loadDirectories(); loadJobs();
    }, [isOpen, loadStatus, loadDirectories, loadJobs]);

    // 실행 중인 작업이 있으면 로그를 이어서 받아온다
    useEffect(() => {
        if (!isOpen || !activeJob?.job || activeJob.job.status !== 'running') return undefined;
        const t = setInterval(async () => {
            try {
                const r = await axios.get(`${UTIL_API}/jobs/${activeJob.job.id}`);
                setActiveJob(r.data);
                if (r.data.job.status !== 'running') loadJobs();
            } catch (_) { /* 폴링 실패는 다음 tick 에 재시도 */ }
        }, 1200);
        return () => clearInterval(t);
    }, [isOpen, activeJob, loadJobs]);

    const handleUpload = async (file) => {
        if (!file) return;
        setBusy(true); setError(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            setUploaded((await axios.post(`${UTIL_API}/upload`, fd)).data);
        } catch (err) { setError(err.response?.data?.error || err.message); }
        finally { setBusy(false); }
    };

    const buildOptions = () => {
        const list = names.split(',').map(s => s.trim()).filter(Boolean);
        if (kind === 'exp') return mode === 'tables' ? { mode, tables: list } : { mode, owners: list };
        if (kind === 'imp') return {
            serverPath: uploaded?.serverPath,
            fromUser: fromUser.trim() || undefined,
            toUser: toUser.trim() || undefined,
            tables: list.length ? list : undefined,
        };
        if (kind === 'expdp') return {
            directory, dumpFile: dumpFile || undefined,
            mode, ...(mode === 'tables' ? { tables: list } : { schemas: list }),
        };
        return { directory, dumpFile, schemas: list.length ? list : undefined, tableExistsAction };
    };

    const handleRun = async () => {
        setBusy(true); setError(null);
        try {
            const res = await axios.post(`${UTIL_API}/jobs`, {
                kind, connectionId, usrid: 'standalone', options: buildOptions(),
            });
            setActiveJob({ job: res.data.job, log: '' });
            loadJobs();
        } catch (err) { setError(err.response?.data?.error || err.message); }
        finally { setBusy(false); }
    };

    const handleCancel = async () => {
        if (!activeJob?.job) return;
        try { await axios.post(`${UTIL_API}/jobs/${activeJob.job.id}/cancel`); }
        catch (err) { setError(err.response?.data?.error || err.message); }
    };

    const utilAvailable = status?.available?.includes(kind);

    if (!isOpen) return null;

    return (
        <div className="outil-overlay" onClick={onClose}>
            <div className="outil-modal" onClick={e => e.stopPropagation()}>
                <div className="outil-header">
                    <h2>{t('dataTransfer:ora.title')}</h2>
                    <button className="outil-close" onClick={onClose}><FiX size={18} /></button>
                </div>

                <div className="outil-kinds">
                    {getKinds(t).map(k => (
                        <button
                            key={k.key}
                            className={`outil-kind ${kind === k.key ? 'active' : ''} ${status && !status.available.includes(k.key) ? 'unavailable' : ''}`}
                            onClick={() => { setKind(k.key); setActiveJob(null); setError(null); }}
                            title={status && !status.available.includes(k.key) ? t('dataTransfer:ora.notFound') : ''}
                        >
                            {k.label}
                        </button>
                    ))}
                </div>

                <div className="outil-body">
                    {/* 클라이언트 탐지 상태 */}
                    {status && (
                        <div className={`outil-status ${status.available.length ? '' : 'bad'}`}>
                            <FiInfo size={12} />
                            {status.available.length
                                ? <span>{t('dataTransfer:ora.client')} <code>{status.binDir}</code> <span className="outil-dim">({status.source})</span></span>
                                : <span>{status.hint}</span>}
                            <button className="outil-mini" onClick={() => loadStatus()} title={t('dataTransfer:ora.redetect')}><FiRefreshCw size={11} /></button>
                        </div>
                    )}

                    {/* 파일 위치 안내 — 이 화면에서 가장 오해하기 쉬운 지점 */}
                    <div className={`outil-where ${isDataPump ? 'server' : 'local'}`}>
                        {isDataPump
                            ? <><FiAlertTriangle size={13} /> {t('dataTransfer:ora.dumpNote1')} <strong>{t('dataTransfer:ora.dbDirectory')}</strong>{t('dataTransfer:ora.dumpNoteServer')}</>
                            : <><FiInfo size={13} /> {t('dataTransfer:ora.dumpNote2')} <strong>{t('dataTransfer:ora.liveSqlServer')}</strong>{t('dataTransfer:ora.dumpNoteLocal')}</>}
                    </div>

                    {isWrite && (
                        <div className="outil-warn">
                            <FiAlertTriangle size={13} />
                            <span><strong>{kind}</strong> {t('dataTransfer:ora.importWarn')}</span>
                        </div>
                    )}

                    <div className="outil-form">
                        <div className="outil-field">
                            <span className="outil-label">{t('dataTransfer:ora.connection')}</span>
                            <span>{connectionName || connectionId || '-'}</span>
                        </div>

                        {(kind === 'exp' || kind === 'expdp') && (
                            <div className="outil-field">
                                <span className="outil-label">{t('dataTransfer:ora.mode')}</span>
                                <select value={mode} onChange={e => setMode(e.target.value)}>
                                    <option value="tables">{t('dataTransfer:ora.tables')}</option>
                                    <option value={kind === 'exp' ? 'owner' : 'schemas'}>{t('dataTransfer:ora.schema')}</option>
                                </select>
                            </div>
                        )}

                        {kind !== 'imp' && (
                            <div className="outil-field">
                                <span className="outil-label">{mode === 'tables' ? t('dataTransfer:ora.tables') : t('dataTransfer:ora.schema')}</span>
                                <input type="text" value={names} placeholder={t('dataTransfer:ora.tablesPlaceholder')}
                                    onChange={e => setNames(e.target.value)} />
                            </div>
                        )}

                        {isDataPump && (
                            <>
                                <div className="outil-field">
                                    <span className="outil-label">DIRECTORY</span>
                                    <select value={directory} onChange={e => setDirectory(e.target.value)}>
                                        <option value="">{t('dataTransfer:ora.optional')}</option>
                                        {directories.directories.map(d => (
                                            <option key={d.name} value={d.name}>{d.name} — {d.path}</option>
                                        ))}
                                    </select>
                                </div>
                                {directories.hint && <div className="outil-hint">{directories.hint}</div>}
                                <div className="outil-field">
                                    <span className="outil-label">{t('dataTransfer:ora.dumpFile')}</span>
                                    <input type="text" value={dumpFile}
                                        placeholder={kind === 'expdp' ? 'expdp_%U.dmp' : t('dataTransfer:ora.serverFileName')}
                                        onChange={e => setDumpFile(e.target.value)} />
                                </div>
                            </>
                        )}

                        {kind === 'imp' && (
                            <>
                                <div className="outil-field">
                                    <span className="outil-label">{t('dataTransfer:ora.uploadDump')}</span>
                                    <input type="file" accept=".dmp" onChange={e => handleUpload(e.target.files[0])} />
                                </div>
                                {uploaded && <div className="outil-hint">{t('dataTransfer:ora.uploaded', { name: uploaded.name, mb: (uploaded.size / 1024 / 1024).toFixed(1) })}</div>}
                                <div className="outil-field">
                                    <span className="outil-label">FROMUSER</span>
                                    <input type="text" value={fromUser} placeholder={t('dataTransfer:ora.optional')} onChange={e => setFromUser(e.target.value)} />
                                </div>
                                <div className="outil-field">
                                    <span className="outil-label">TOUSER</span>
                                    <input type="text" value={toUser} placeholder={t('dataTransfer:ora.optional')} onChange={e => setToUser(e.target.value)} />
                                </div>
                                <div className="outil-field">
                                    <span className="outil-label">{t('dataTransfer:ora.tables')}</span>
                                    <input type="text" value={names} placeholder={t('dataTransfer:ora.optionalComma')} onChange={e => setNames(e.target.value)} />
                                </div>
                            </>
                        )}

                        {kind === 'impdp' && (
                            <div className="outil-field">
                                <span className="outil-label">{t('dataTransfer:ora.existingTable')}</span>
                                <select value={tableExistsAction} onChange={e => setTableExistsAction(e.target.value)}>
                                    <option value="SKIP">{t('dataTransfer:ora.skip')}</option>
                                    <option value="APPEND">{t('dataTransfer:ora.append')}</option>
                                    <option value="TRUNCATE">{t('dataTransfer:ora.truncate')}</option>
                                    <option value="REPLACE">{t('dataTransfer:ora.replace')}</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {error && <div className="outil-error">{error}</div>}

                    {/* 실행 로그 */}
                    {activeJob?.job && (
                        <div className="outil-job">
                            <div className="outil-job-head">
                                <span className={`outil-badge ${activeJob.job.status}`}>{activeJob.job.status}</span>
                                <span className="outil-dim">{activeJob.job.label}</span>
                                {activeJob.job.exitCode != null && <span className="outil-dim">exit={activeJob.job.exitCode}</span>}
                                <span className="outil-dim">{(activeJob.job.elapsedMs / 1000).toFixed(1)}s</span>
                                <div className="outil-spacer" />
                                {activeJob.job.status === 'running' && (
                                    <button className="outil-btn outil-btn-small" onClick={handleCancel}>{t('dataTransfer:ora.stop')}</button>
                                )}
                            </div>
                            <pre className="outil-log">{activeJob.log || t('dataTransfer:ora.waitingOutput')}</pre>
                            {activeJob.job.files?.length > 0 && (
                                <div className="outil-files">
                                    {activeJob.job.files.map(f => (
                                        <a key={f.name}
                                            className="outil-file"
                                            href={`${UTIL_API}/jobs/${activeJob.job.id}/files/${encodeURIComponent(f.name)}`}
                                        >
                                            <FiDownload size={11} /> {f.name} <span className="outil-dim">({(f.size / 1024).toFixed(0)}KB)</span>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 최근 작업 */}
                    {jobs.length > 0 && (
                        <details className="outil-history">
                            <summary>{t('dataTransfer:ora.recentJobs', { n: jobs.length })}</summary>
                            <div className="outil-history-list">
                                {jobs.slice(0, 10).map(j => (
                                    <button key={j.id} className="outil-history-row"
                                        onClick={async () => setActiveJob((await axios.get(`${UTIL_API}/jobs/${j.id}`)).data)}>
                                        <span className={`outil-badge ${j.status}`}>{j.status}</span>
                                        <span className="outil-mono">{j.kind}</span>
                                        <span className="outil-dim outil-ellipsis">{j.label}</span>
                                        <span className="outil-dim">{formatTime(j.startedAt)}</span>
                                    </button>
                                ))}
                            </div>
                        </details>
                    )}
                </div>

                <div className="outil-footer">
                    <button className="outil-btn" onClick={onClose}>{t('dataTransfer:ora.close')}</button>
                    <div className="outil-spacer" />
                    <button
                        className={`outil-btn ${isWrite ? 'outil-btn-danger' : 'outil-btn-primary'}`}
                        onClick={handleRun}
                        disabled={busy || !connectionId || !utilAvailable || (kind === 'imp' && !uploaded)}
                    >
                        {isWrite ? <FiUpload size={13} /> : <FiPlay size={13} />} {busy ? t('dataTransfer:ora.starting') : t('dataTransfer:ora.runKind', { kind })}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OracleUtilModal;
